import React from 'react';
import Navbar from '@/components/feature/Navbar';

export interface DetailPageLayoutProps {
  /** 页面顶部的 Hero / Breadcrumb / Banner 区域 */
  header?: React.ReactNode;
  /** 主要内容区域 (如描述、图文、试看播放器、动态正文等) */
  children: React.ReactNode;
  /** 侧边栏内容区域 (如信息概览、企业卡片、时间线、课时目录等) */
  sidebar?: React.ReactNode;
  /** 评论区 (最底部) */
  comments?: React.ReactNode;
  /** 底部扩展区域 (如同类推荐、相关帖子、返回按钮等) */
  extra?: React.ReactNode;
  /** 页面包含的 Modal 弹窗或全局浮层 */
  modals?: React.ReactNode;
  /** 外层整体背景样式类，默认为 'bg-white' */
  bgClass?: string;
  /** 内层 detail-shell 自定义类名 */
  shellClass?: string;
  /** 侧边栏宽度控制，默认为 'lg:w-[340px] xl:w-[360px]' */
  sidebarWidth?: string;
  /** 是否隐藏 Navbar，默认为 false */
  noNavbar?: boolean;
}

/**
 * 统一详情页布局组件 (DetailPageLayout)
 * 
 * 布局策略:
 * 1. 顶部 Navbar + Width Header / Hero 区域
 * 2. 桌面端 (lg: 及以上): 2 栏 Grid 布局
 *    - 左栏 (col-1): children (主体内容) -> extra (扩展推荐) -> comments (评论区)
 *    - 右栏 (col-2): sidebar (sticky 侧边栏)
 * 3. 移动端/小屏幕 (< lg): 1 栏垂直堆叠 (grid-cols-1)，顺序为:
 *    Header -> Children (主体内容) -> Sidebar (侧边栏卡片) -> Extra (扩展推荐/操作按钮) -> Comments (最底部评论区)
 */
export const DetailPageLayout: React.FC<DetailPageLayoutProps> = ({
  header,
  children,
  sidebar,
  comments,
  extra,
  modals,
  bgClass = 'bg-white',
  shellClass = '',
  sidebarWidth = 'lg:w-[340px] xl:w-[360px]',
  noNavbar = false,
}) => {
  return (
    <div className={`min-h-screen flex flex-col ${bgClass}`}>
      {!noNavbar && <Navbar />}

      {/* 顶部 Header / Hero 区域 */}
      {header}

      {/* 核心容器 */}
      <main className={`detail-shell py-6 sm:py-8 flex-1 flex flex-col gap-6 sm:gap-8 ${shellClass}`}>
        {sidebar ? (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-8 items-start">
            {/* 主体内容 (桌面端左栏 col-1) */}
            <div className="w-full min-w-0 lg:col-start-1 space-y-6 sm:space-y-8">
              {children}
            </div>

            {/* 侧边栏内容 (桌面端右栏 col-2，移动端紧随 children 之后) */}
            <aside className={`w-full ${sidebarWidth} shrink-0 lg:col-start-2 lg:row-start-1 lg:row-span-3 lg:sticky lg:top-24 space-y-5 sm:space-y-6`}>
              {sidebar}
            </aside>

            {/* 评论区 (桌面端左栏最下方 col-1，移动端在 sidebar 之后) */}
            {comments && (
              <div className="w-full min-w-0 lg:col-start-1 space-y-6 sm:space-y-8">
                {comments}
              </div>
            )}
          </div>
        ) : (
          <div className="w-full space-y-6 sm:space-y-8">
            {children}
            {comments && <div className="w-full space-y-6 sm:space-y-8">{comments}</div>}
          </div>
        )}

        {/* 底部扩展推荐区 (最底部，位于评论区下方，跨全宽渲染) */}
        {extra && (
          <div className="w-full space-y-6 sm:space-y-8">
            {extra}
          </div>
        )}
      </main>

      {/* Modal / 弹窗浮层 */}
      {modals}
    </div>
  );
};

export default DetailPageLayout;
